import React from 'react';
import clsx from 'clsx';

import useAutocomplete, {
  UseAutocompleteProps
} from '../hooks/useAutocomplete';

import { useSearchListContext } from '../context/SearchListProvider';

type AutocompleteProps = UseAutocompleteProps;

const Autocomplete = ({ ...props }: AutocompleteProps) => {
  const { i18n, suggestions } = useSearchListContext();
  const { autocomplete, autocompleteState } = useAutocomplete({
    suggestions,
    ...props
  });
  const inputRef = React.useRef(null);

  const isSearchStalled = autocompleteState.status === 'stalled';

  return (
    <div
      className="search-list__autocomplete"
      {...autocomplete.getRootProps({})}
    >
      <form
        className="search-list__input"
        {...autocomplete.getFormProps({ inputElement: inputRef.current })}
      >
        <div className="search-list__input-wrapper">
          <div className="search-list__input-inner">
            <input
              ref={inputRef}
              className="typo-button"
              {...autocomplete.getInputProps({
                inputElement: inputRef.current
              })}
              placeholder={i18n.searchQuery}
            />

            <button
              type="reset"
              hidden={autocompleteState.query.length === 0 || isSearchStalled}
              title={i18n.resetSearch}
            >
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.4973 13.6518L2.31374 22.4467L0.679688 20.5547L10.5849 12.0002L0.680923 3.44675L2.31497 1.55469L12.4973 10.3485L22.6797 1.55469L24.3137 3.44675L14.4098 12.0002L24.315 20.5547L22.6809 22.4467L12.4973 13.6518Z"
                />
              </svg>
            </button>
          </div>

          <button
            type="submit"
            className="button button--black button--back button--has-icon button--reverse"
            title={i18n.submitSearch}
          >
            <span className="button__icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M7.49581 3L12.575 8.07914L7.49581 13.1583L6.67086 12.3333L10.3417 8.66247L3 8.66248V7.49581L10.3417 7.49581L6.67086 3.82496L7.49581 3Z" />
              </svg>
            </span>
            <span className="typo-button button__label">
              {i18n.submitSearch}
            </span>
          </button>

          <div
            className="search-list__autocomplete-panel"
            {...autocomplete.getPanelProps({})}
          >
            {autocompleteState.isOpen &&
              autocompleteState.collections.map((collection) => {
                const { source, items } = collection;

                return (
                  <div
                    key={source.sourceId}
                    className="search-list__autocomplete-layout"
                  >
                    <div className="search-list__autocomplete-source">
                      {items.length > 0 && (
                        <ul
                          className="search-list__autocomplete-list"
                          {...autocomplete.getListProps()}
                        >
                          {items.map((item) => {
                            return (
                              <li
                                key={item.title}
                                className="search-list__autocomplete-item typo-button"
                                {...autocomplete.getItemProps({
                                  item,
                                  source
                                })}
                              >
                                <span
                                  className="ais-Highlight"
                                  dangerouslySetInnerHTML={{
                                    __html: item.title
                                  }}
                                />
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {suggestions && suggestions.length !== 0 && (
          <div
            className={clsx('search-list__suggestions', {
              'hide-visually': autocompleteState.query
            })}
          >
            {suggestions.slice(0, 5).map((item) => (
              <button
                key={item.title}
                type="submit"
                name={item.title}
                value={item.title}
                className="tag tag--white"
                onClick={() => {
                  autocomplete.setQuery(item.title);
                }}
              >
                {item.title}
              </button>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default Autocomplete;
